import React from 'react';
import { Loader, Check, HelpCircle } from 'lucide-react';
import { useContentPlanStore } from '../../../../stores/contentPlanStore';
import type { Question, PlanAnswers } from './types';

interface PlanQuestionnaireProps {
  questions: Question[];
  answers: PlanAnswers;
  setAnswers: (answers: PlanAnswers) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  isGenerating: boolean;
}

export default function PlanQuestionnaire({
  questions,
  answers,
  setAnswers,
  onSubmit,
  isGenerating
}: PlanQuestionnaireProps) {
  const { canCreatePlan, incrementPlanUsage } = useContentPlanStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canCreatePlan()) {
      return;
    }

    // Increment usage before generating plan
    if (!incrementPlanUsage()) {
      return;
    }

    await onSubmit(e);
  };

  const handleInputChange = (id: keyof PlanAnswers, value: string) => {
    setAnswers({ ...answers, [id]: value });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {questions.map((q, index) => (
        <div key={q.id} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm transition-all hover:shadow-md">
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium">
              {index + 1}
            </div>
            <label className="text-lg font-medium text-gray-800">
              {q.question}
            </label>
            <div className="group relative">
              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute left-0 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                {q.id === 'objective' && "Defina claramente o que você deseja alcançar com seu conteúdo, como educar, entreter ou vender."}
                {q.id === 'contentType' && "Especifique os formatos que você utiliza, como vídeos, posts, stories, etc."}
                {q.id === 'bestPerforming' && "Identifique qual tipo de conteúdo gera mais engajamento com seu público."}
                {q.id === 'targetAudience' && "Descreva quem é seu público-alvo, incluindo idade, interesses e comportamentos."}
              </div>
            </div>
          </div>
          <textarea
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
            rows={3}
            placeholder={q.placeholder}
            value={answers[q.id]}
            onChange={(e) => handleInputChange(q.id, e.target.value)}
          />
        </div>
      ))}

      <button
        type="submit"
        disabled={isGenerating || !canCreatePlan()}
        className={`w-full py-3 px-4 rounded-lg transition-colors flex items-center justify-center ${
          canCreatePlan()
            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
            : 'bg-gray-100 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isGenerating ? (
          <>
            <Loader className="animate-spin h-5 w-5 mr-2" />
            Gerando seu plano...
          </>
        ) : (
          <>
            Gerar Plano
            <Check className="ml-2 h-5 w-5" />
          </>
        )}
      </button>
    </form>
  );
}